import { useLazyQuery } from "@apollo/client";
import { useKeycloak } from "@react-keycloak/web";
import React, { useEffect, useState } from "react";

import { useCompanyId } from "./components/hooks/useCompanyId";
import Routes from "./components/Routes";
import {
  GetJoinRequestsForUserResponse,
  GetJoinRequestsForUserVariables,
  getJoinRequestsForUser,
} from "./graphqlQueries/getJoinRequests";
import { DatahubAccessToken, UserInfo, UserRole } from "./types";
import { TDH_COMPANY_ID } from "./utils/constants";
import {
  getHasuraRoleContext,
  isCompanyAllowedForUser,
  isUserWithoutOrganisation,
  isVisitFinlandAdmin,
} from "./utils/functions";
import { userInfoVar } from "./utils/ApolloCache";

enum UserCompanyStatus {
  NotInitialized,
  NoCompany,
  HasCompany,
}

type AppContainerState = {
  dataInitialized: boolean;
  authenticated: boolean;
  hasCompany: UserCompanyStatus;
  companyRequired: boolean;
};

const AuthenticatedAppContainer = () => {
  const { keycloak } = useKeycloak();
  const [appState, setAppState] = useState<AppContainerState>({
    authenticated: false,
    companyRequired: true,
    dataInitialized: false,
    hasCompany: UserCompanyStatus.NotInitialized,
  });

  const [companyId, setCompanyId] = useCompanyId();
  const [userWithoutOrganisation, setUserWithoutOrganisation] = useState<boolean>(false);
  const [adminUser, setAdminUser] = useState<boolean>(false);

  const [
    getJoinRequests,
    { data: joinRequestsForUser, loading: joinRequestsLoading, called: joinRequestsCalled },
  ] = useLazyQuery<GetJoinRequestsForUserResponse, GetJoinRequestsForUserVariables>(
    getJoinRequestsForUser
  );

  useEffect(() => {
    if (keycloak.authenticated && keycloak.tokenParsed) {
      const accessToken = keycloak.tokenParsed as DatahubAccessToken;

      const userInfo: UserInfo = {
        businessId: accessToken.business_id,
        companyIds: accessToken.company_ids,
        email: accessToken.email,
        firstName: accessToken.given_name,
        fullName: accessToken.name,
        lastName: accessToken.family_name,
        roleGroups: accessToken.groups,
        userId: accessToken.sub,
      };

      // Initializes apollo client, do not delete
      userInfoVar(userInfo);

      const companyRequired = !!userInfo.businessId;
      const usersFirstCompany = userInfo.companyIds?.[0];

      setUserWithoutOrganisation(isUserWithoutOrganisation(userInfo));
      setAdminUser(isVisitFinlandAdmin(keycloak));

      if (companyRequired) {
        getJoinRequests({
          context: getHasuraRoleContext(UserRole.CreateCompany),
          variables: {
            userId: userInfo.userId || "",
          },
        });
      }

      const initData = () => {
        const storedCompanyId = localStorage.getItem(TDH_COMPANY_ID);
        const storedCompanyIsValid = isCompanyAllowedForUser(userInfo.companyIds, storedCompanyId);
        if (storedCompanyId !== null && storedCompanyIsValid) {
          setCompanyId(storedCompanyId);
        } else if (usersFirstCompany) {
          setCompanyId(usersFirstCompany);
        } else {
          setCompanyId("");
          localStorage.removeItem(TDH_COMPANY_ID);
        }
        setAppState((state) => {
          return {
            ...state,
            companyRequired,
            dataInitialized: true,
          };
        });
      };

      initData();
    }
  }, [keycloak, setCompanyId, getJoinRequests]);

  useEffect(() => {
    if (companyId !== null) {
      setAppState((state) => {
        return {
          ...state,
          authenticated: Boolean(keycloak.authenticated),
          hasCompany: companyId !== "" ? UserCompanyStatus.HasCompany : UserCompanyStatus.NoCompany,
        };
      });
    }
  }, [companyId, keycloak]);

  const hasJoinRequests = (joinRequestsForUser?.companyJoinRequest.length ?? 0) !== 0;

  const authenticatedAndInitialized =
    appState.dataInitialized &&
    appState.authenticated &&
    appState.hasCompany !== UserCompanyStatus.NotInitialized;

  if (!authenticatedAndInitialized) {
    return null;
  }
  if (companyId === null) {
    return null;
  }

  if (joinRequestsLoading && joinRequestsCalled) {
    return null;
  }

  return (
    <Routes
      authenticated={appState.authenticated}
      hasCompany={appState.hasCompany === UserCompanyStatus.HasCompany}
      companyRequired={appState.companyRequired}
      companyId={companyId}
      hasJoinRequests={hasJoinRequests}
      userWithoutOrganisation={userWithoutOrganisation}
      isAdminUser={adminUser}
    />
  );
};

export default AuthenticatedAppContainer;
